import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RestProviderService } from '../../../services/rest-provider/rest-provider.service';

interface MetaRow {
  label: string;
  value: string;
  status?: string;
}

interface FieldChange {
  label: string;
  oldValue: string;
  newValue: string;
  kind: 'added' | 'removed' | 'scalar';
}

interface CollectionChange {
  label: string;
  oldCount: number;
  newCount: number;
  items: { type: string; summary: string; title?: string }[];
}

interface ChangeLogEntry {
  date: string;
  /** Raw LogDateTime (ISO) for chronological sorting - `date` is a localized
      display string and MUST NOT be compared lexicographically. */
  ts: string;
  description: string;
  user: string;
  color: string;
  changes: FieldChange[];
  collectionChanges: CollectionChange[];
  expanded?: boolean;
}

@Component({
  selector: 'app-wg-metadata-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './wg-metadata-panel.component.html',
  styleUrls: ['./wg-metadata-panel.component.css']
})
export class WgMetadataPanelComponent implements OnInit, OnDestroy {

  readonly expandedSections = signal<Set<string>>(new Set(['lastnik', 'datumi', 'dnevnik']));

  identification: MetaRow[] = [];
  /**
   * Klasifikacijski znak (PETZ 17 / UVDAG), rendered as one more row of IDENTIFIKACIJA.
   *
   * Null means WE COULD NOT ASK - the classification tables are not installed here, or the
   * lookup failed - and the row is left out, because we have nothing to say about the record.
   * A record that simply carries no stamp is a different thing: the endpoint answers
   * Available with Classified false, and the row renders 'ni razvrsceno'. Hiding it in that
   * case would make an unclassified record indistinguishable from a build without the feature.
   */
  classification: MetaRow | null = null;
  owner: MetaRow[] = [];
  dates: MetaRow[] = [];
  changeLog: ChangeLogEntry[] = [];
  loaded = false;

  private modelSub: Subscription | null = null;
  private currentRecordId: number | null = null;

  constructor(private restProvider: RestProviderService) {}

  ngOnInit() {
    this.modelSub = this.restProvider.modelValueChanged.subscribe((model: any) => {
      if (!model || !model.Id) return;

      const recordId = model.Id;
      const formName = model.Title || model.SerializationObjectFormLink || '';

      this.identification = [
        { label: 'ID zapisa', value: String(recordId) },
        { label: 'Obrazec', value: formName },
      ];

      const version = model.Version || model.TimestampInt || model.Verzija;
      if (version) {
        this.identification.push({ label: 'Verzija', value: String(version) });
      }

      const status = model.DocumentStatus || model.Status;
      if (status) {
        const statusName = typeof status === 'object' ? (status.Name || status.Title || '') : String(status);
        const isActive = statusName.toLowerCase().includes('aktiv') || statusName.toLowerCase().includes('active');
        this.identification.push({
          label: 'Status',
          value: statusName,
          status: isActive ? 'active' : undefined
        });
      }

      this.owner = [];
      const creator = model.DocumentCreator || model.Creator;
      if (creator) {
        const creatorName = typeof creator === 'object'
          ? (creator.FullName || creator.Username || creator.Name || '')
          : String(creator);
        this.owner.push({ label: 'Uporabnik', value: creatorName });

        if (typeof creator === 'object' && creator.Role) {
          this.owner.push({ label: 'Vloga', value: creator.Role });
        }
        if (typeof creator === 'object' && creator.Organization) {
          this.owner.push({ label: 'Organizacija', value: creator.Organization });
        }
      }

      this.dates = [];
      const entryDate = model.EntryDateString || model.EntryDate;
      if (entryDate) {
        this.dates.push({ label: 'Ustvarjeno', value: this.formatDate(entryDate) });
      }
      if (creator) {
        const creatorName = typeof creator === 'object'
          ? (creator.FullName || creator.Username || creator.Name || '')
          : String(creator);
        this.dates.push({ label: 'Ustvaril', value: creatorName });
      }

      const lastChange = model.LastChangeDateString || model.LastChangeDate;
      if (lastChange) {
        this.dates.push({ label: 'Zadnja spr.', value: this.formatDate(lastChange) });
      }
      const lastEditor = model.LastEditedUser || model.LastEditor;
      if (lastEditor) {
        const editorName = typeof lastEditor === 'object'
          ? (lastEditor.FullName || lastEditor.Username || lastEditor.Name || '')
          : String(lastEditor);
        this.dates.push({ label: 'Spremenil', value: editorName });
      }

      this.loaded = true;

      if (recordId !== this.currentRecordId) {
        this.currentRecordId = recordId;
        this.loadAuditLog(recordId);
        this.loadClassification(recordId);
      }
    });
  }

  ngOnDestroy() {
    if (this.modelSub) {
      this.modelSub.unsubscribe();
    }
  }

  toggleEntry(entry: ChangeLogEntry) {
    entry.expanded = !entry.expanded;
  }

  /**
   * Reads the stamp off the record itself, through its own small endpoint.
   *
   * ClassificationPlanId is on the domain Document and not on DocumentModel, and the record
   * envelope returns fields per-form projection - so it does not reach this panel with the
   * model, and putting it there would mean adding it to every form. This mirrors how the
   * change log below is fetched.
   *
   * Every failure path leaves classification null and the row simply absent. This is an
   * optional field on a record view that has to keep working without it.
   */
  private loadClassification(recordId: number) {
    this.classification = null;

    this.restProvider.getDataGET('api/classification/for-record/' + recordId + '/')
      .pipe(catchError(() => of(null)))
      .subscribe((raw: any) => {
        if (raw === null || raw === undefined) return;
        let data: any;
        try {
          data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch {
          return;
        }
        if (!data || data.Available !== true) return;

        let value: string;
        if (!data.Classified) {
          value = 'ni razvrščeno';
        } else if (data.Code) {
          value = data.Name ? data.Code + ' - ' + data.Name : String(data.Code);
        } else {
          // A stamp whose class could not be read. Reporting the id is still true; calling
          // a stamped record unclassified would not be.
          value = 'ID ' + data.PlanId;
        }

        this.classification = { label: 'Klasifikacijski znak', value: value };
      });
  }

  private loadAuditLog(recordId: number) {
    this.changeLog = [];

    const base = 'api/auditlog/retrieve/' + recordId + '/';
    const empty = of('[]');

    forkJoin({
      updates: this.restProvider.getDataGET(base + 'update').pipe(catchError(() => empty)),
      inserts: this.restProvider.getDataGET(base + 'insert').pipe(catchError(() => empty)),
      customactions: this.restProvider.getDataGET(base + 'customaction').pipe(catchError(() => empty)),
    }).subscribe(({ updates, inserts, customactions }) => {
      const mapped: ChangeLogEntry[] = [];

      try {
        const updateEntries = JSON.parse(updates as string);
        for (const entry of updateEntries) {
          const result = this.computeEntryChanges(entry);
          // Skip level 1 entries with no parseable changes
          if (result.changes.length === 0 && result.collectionChanges.length === 0 && result.description.includes('brez podrobnosti')) continue;
          mapped.push({
            date: this.formatDateTime(entry.LogDateTime),
            ts: entry.LogDateTime || '',
            description: result.description,
            user: entry.Username || '',
            color: '#3b82f6',
            changes: result.changes,
            collectionChanges: result.collectionChanges
          });
        }
      } catch (e) {}

      try {
        const insertEntries = JSON.parse(inserts as string);
        for (const entry of insertEntries) {
          mapped.push({
            date: this.formatDateTime(entry.LogDateTime),
            ts: entry.LogDateTime || '',
            description: 'Zapis ustvarjen',
            user: entry.Username || '',
            color: '#64748b',
            changes: [],
            collectionChanges: []
          });
        }
      } catch (e) {}

      try {
        const actionEntries = JSON.parse(customactions as string);
        for (const entry of actionEntries) {
          mapped.push({
            date: this.formatDateTime(entry.LogDateTime),
            ts: entry.LogDateTime || '',
            description: entry.EntityShortName || 'Akcija izvedena',
            user: entry.Username || '',
            color: '#22c55e',
            changes: [],
            collectionChanges: []
          });
        }
      } catch (e) {}

      // Newest first, on the raw ISO timestamp - the localized display date does
      // not sort chronologically as a string ("1. 09." vs "10. 08.").
      mapped.sort((a, b) => b.ts.localeCompare(a.ts));
      this.changeLog = mapped;
    });
  }

  // ── Change computation (mirrors platform audit-log-detail logic) ──

  private static SYSTEM_FIELDS = new Set([
    'Version', 'EntryDate', 'EntryDateString', 'LastChangeDate', 'LastChangeDateString',
    'SerializationObjectFormLink', 'DocumentType', 'LastIndexUpdate', 'RepositoryId',
    'SourceRepository', 'ClientId', 'Order', 'CurrentLoggedInUser', 'DetailsFormUrl',
    'DocumentId', 'Deleted', 'DeletedOn', 'DeletedBy', 'AnnotationsString', 'AddedFiles',
    'SerializationObjectTypeData', 'DocumentCreatorString', 'DocumentEntryDateString',
    'GalisDocumentType', 'GalisDocumentTypeString', 'HasImage', 'HasImageInt',
    'FirstMediaId', 'FirstItemImage', 'FirstItemImageDocument', 'FirstImageId',
    'SearchableInvNum', 'AuthorsAsString', 'ClassificationsAsString', 'MaterialsAsString',
    'TechniquesAsString', 'ItemTypeName', 'CollectionName', 'MeasurementsFormatted',
    'PrettyInvNum', 'PrettyAccNum', 'DocumentationAsString', 'WeightText',
    'DocumentIdentifier', 'InstitutionID', 'Institution'
  ]);

  private static LABELS: Record<string, string> = {
    ObjectTitle: 'Naslov',
    ObjectTitleAddition: 'Dodatek naslova',
    ObjectName: 'Ime predmeta',
    ObjectNameAddition: 'Dodatek imena',
    InvNumber: 'Inv. številka',
    AccNumber: 'Akc. številka',
    OldInvNumber: 'Stara inv. št.',
    TimeOfOrigin: 'Čas nastanka',
    TimeOfOriginFrom: 'Čas od',
    TimeOfOriginTo: 'Čas do',
    PlaceOfOrigin: 'Kraj nastanka',
    Description: 'Opis',
    Comment: 'Komentar',
    ContentMotif: 'Vsebina / Motiv',
    ObjectHistory: 'Zgodovina predmeta',
    Signature: 'Signatura',
    SignaturePosition: 'Položaj signature',
    Writings: 'Napisi',
    StateDescription: 'Opis stanja',
    ObjectCompleteness: 'Celovitost',
    Literature: 'Literatura',
    Provenience: 'Provenienca',
    Clarification: 'Opombe',
    IdentifierTitle: 'Prikazni naslov',
    Title: 'Naslov',
    OriginPeriod: 'Obdobje',
    AcquisitionMethod: 'Način pridobitve',
    AcquisitionTime: 'Datum pridobitve',
    PreviousOwner: 'Prejšnji lastnik',
    EstimatedValue: 'Ocenjena vrednost',
    PurchaseValue: 'Nabavna vrednost',
    AccountNumber: 'Številka računa',
    NumberOfObjects: 'Število predmetov',
    Owner: 'Lastnik',
    ForeignIdentifier: 'Tuja identifikacija',
    FindSite: 'Najdišče',
    FindPerson: 'Najditelj',
    LocationOfDiscovery: 'Lokacija odkritja',
    TimeOfDiscovery: 'Datum odkritja',
    PublishedOnMuseums: 'Objavljeno na spletu',
    Originality: 'Izvirnost',
    Medias: 'Datoteke',
    Item2Authors: 'Avtorji',
    Authors: 'Avtorji',
    Item2Materials: 'Materiali',
  };

  private computeEntryChanges(entry: any): { description: string; changes: FieldChange[]; collectionChanges: CollectionChange[] } {
    const onv = this.parseLogData(entry);
    if (!onv || !onv.Old || !onv.New) {
      // Level 1 audit entries only have REQUEST data, no old/new diff
      return { description: 'Zapis spremenjen (brez podrobnosti)', changes: [], collectionChanges: [] };
    }

    const oldObj = onv.Old;
    const newObj = onv.New;
    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
    const changes: FieldChange[] = [];
    const collectionChanges: CollectionChange[] = [];

    for (const key of allKeys) {
      if (WgMetadataPanelComponent.SYSTEM_FIELDS.has(key)) continue;

      const ov = oldObj[key];
      const nv = newObj[key];

      if (JSON.stringify(ov) === JSON.stringify(nv)) continue;

      // Nested objects — only flag if Id changed
      if (this.isPlainObject(ov) && this.isPlainObject(nv)) {
        const oldId = ov?.Id ?? ov?.id;
        const newId = nv?.Id ?? nv?.id;
        if (oldId !== newId) {
          changes.push({
            label: this.getLabel(key),
            oldValue: this.summarizeObject(ov),
            newValue: this.summarizeObject(nv),
            kind: 'scalar'
          });
        }
        continue;
      }

      // Arrays — collection changes
      if (Array.isArray(ov) || Array.isArray(nv)) {
        const oldArr = Array.isArray(ov) ? ov : [];
        const newArr = Array.isArray(nv) ? nv : [];
        if (JSON.stringify(oldArr) !== JSON.stringify(newArr)) {
          collectionChanges.push(this.diffArray(key, oldArr, newArr));
        }
        continue;
      }

      // Scalar change
      let kind: FieldChange['kind'] = 'scalar';
      if (ov === null || ov === undefined || ov === '') kind = 'added';
      else if (nv === null || nv === undefined || nv === '') kind = 'removed';

      changes.push({
        label: this.getLabel(key),
        oldValue: this.formatValue(ov),
        newValue: this.formatValue(nv),
        kind
      });
    }

    // Sort: alphabetical
    changes.sort((a, b) => a.label.localeCompare(b.label));

    const totalChanges = changes.length + collectionChanges.length;
    const desc = totalChanges > 0
      ? totalChanges + (totalChanges === 1 ? ' sprememba' : totalChanges <= 4 ? ' spremembe' : ' sprememb')
      : 'Zapis spremenjen';

    return { description: desc, changes, collectionChanges };
  }

  private diffArray(key: string, oldArr: any[], newArr: any[]): CollectionChange {
    const label = this.getLabel(key);
    const items: { type: string; summary: string; title?: string }[] = [];

    const oldById = new Map(oldArr.filter(o => this.identityOf(o)).map(o => [this.identityOf(o), o]));
    const newById = new Map(newArr.filter(o => this.identityOf(o)).map(o => [this.identityOf(o), o]));

    for (const [id, oldItem] of oldById) {
      if (!newById.has(id)) {
        items.push({ type: 'removed', ...this.describeItem(oldItem) });
      } else {
        const newItem = newById.get(id);
        if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
          const fieldDiffs: string[] = [];
          for (const k of new Set([...Object.keys(oldItem), ...Object.keys(newItem)])) {
            if (JSON.stringify(oldItem[k]) !== JSON.stringify(newItem[k]) && !this.isPlainObject(oldItem[k]) && !Array.isArray(oldItem[k])) {
              fieldDiffs.push(k);
            }
          }
          const described = this.describeItem(oldItem);
          items.push({ type: 'modified', summary: described.summary + ' — ' + (fieldDiffs.join(', ') || 'spremenjen'), title: described.title });
        }
      }
    }

    for (const [id, newItem] of newById) {
      if (!oldById.has(id)) {
        items.push({ type: 'added', ...this.describeItem(newItem) });
      }
    }

    return { label, oldCount: oldArr.length, newCount: newArr.length, items };
  }

  private parseLogData(entry: any): { Old?: any; New?: any } | null {
    if (!entry.LogData) return null;
    const raw = entry.LogData;

    try {
      const parsed = JSON.parse(raw);
      if (parsed.Old && parsed.New) return parsed;
    } catch {}

    try {
      if (raw.includes('OLD_NEW_VALUES =>')) {
        let onvStr = raw.split('OLD_NEW_VALUES =>')[1].trim();
        onvStr = onvStr.replace(/\r?\n?$/, '').trim();
        const onv = JSON.parse(onvStr);
        if (onv.Old && onv.New) return onv;
      }
    } catch {}

    return null;
  }

  private isPlainObject(v: any): boolean {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
  }

  /**
   * The audit payload reaches this panel in two shapes. The media API writes camelCase
   * (fileId, fileName); the record save envelope writes PascalCase (FileID, Name) and
   * names its join collections differently again (Authors carries Item2AuthorId, no Id).
   * Every one of those used to be dropped here, which is why a collection change rendered
   * as a bare count with no items underneath.
   */
  private identityOf(o: any): any {
    if (!o || typeof o !== 'object') return undefined;
    if (o.Id) return o.Id;
    if (o.fileId) return o.fileId;
    if (o.id) return o.id;
    if (o.FileID) return o.FileID;
    for (const k of Object.keys(o)) {
      const v = o[k];
      if (/Id$/i.test(k) && v && (typeof v === 'number' || typeof v === 'string')) return k + ':' + v;
    }
    return undefined;
  }

  private isMediaDescriptor(obj: any): boolean {
    return !!obj && (obj.fileName !== undefined || obj.fileId !== undefined || obj.FileID !== undefined);
  }

  private formatFileSize(bytes: any): string {
    const n = Number(bytes);
    if (bytes === null || bytes === undefined || !isFinite(n) || n < 0) return '';
    if (n < 1000) return n + ' B';
    const units = ['kB', 'MB', 'GB', 'TB'];
    let v = n / 1000;
    let i = 0;
    while (v >= 1000 && i < units.length - 1) { v /= 1000; i++; }
    return (v < 100 ? v.toFixed(1) : String(Math.round(v))).replace('.', ',') + ' ' + units[i];
  }

  /**
   * The same SHA-256 reaches us hex-encoded on one path and base64 on the other.
   * Normalise to hex so the same file reads identically wherever it appears.
   */
  private normaliseChecksum(sum: any): string {
    if (typeof sum !== 'string' || !sum) return '';
    if (/^[0-9a-fA-F]{64}$/.test(sum)) return sum.toLowerCase();
    if (/^[A-Za-z0-9+/]{43}=$/.test(sum)) {
      try {
        const bin = atob(sum);
        if (bin.length !== 32) return sum;
        let hex = '';
        for (let i = 0; i < bin.length; i++) hex += bin.charCodeAt(i).toString(16).padStart(2, '0');
        return hex;
      } catch { return sum; }
    }
    return sum;
  }

  private abbreviateChecksum(sum: string): string {
    return sum.length > 16 ? sum.slice(0, 8) + '\u2026' + sum.slice(-5) : sum;
  }

  /**
   * Visible one-liner plus the full, unabbreviated detail for the title attribute.
   * A truncated hash that cannot be read in full is not evidence (PETZ cl. 151).
   */
  private describeItem(obj: any): { summary: string; title: string } {
    if (this.isMediaDescriptor(obj)) {
      const fileId = obj.fileId ?? obj.FileID;
      const name = obj.fileName || obj.Name || ('ID: ' + (fileId ?? '?'));
      const size = this.formatFileSize(obj.sizeBytes ?? obj.SizeBytes);
      const sum = this.normaliseChecksum(obj.checksum ?? obj.FileChecksum);
      const mime = obj.mimeType || obj.MimeType;
      const signature = obj.systemSignature || obj.SystemSignature;
      const path = obj.storagePath || obj.FileDataBackendInfo;
      const rawSize = obj.sizeBytes ?? obj.SizeBytes;

      const parts: string[] = [name];
      if (size) parts.push(size);
      if (sum) parts.push('sha256 ' + this.abbreviateChecksum(sum));

      const detail: string[] = [name];
      if (mime) detail.push('Vrsta: ' + mime);
      if (size) detail.push('Velikost: ' + size + ' (' + rawSize + ' B)');
      if (sum) detail.push('Kontrolna vsota SHA-256: ' + sum);
      if (signature) detail.push('Podpis sistema: ' + signature);
      if (path) detail.push('Hramba: ' + path);
      if (fileId !== undefined && fileId !== null) detail.push('ID datoteke: ' + fileId);

      return { summary: parts.join(' \u00b7 '), title: detail.join('\n') };
    }
    let summary = this.summarizeObject(obj);
    if (summary === 'ID: ?') {
      const id = this.identityOf(obj);
      if (id !== undefined) summary = 'ID: ' + id;
    }
    return { summary, title: summary };
  }

  private summarizeObject(obj: any): string {
    if (!obj) return '—';
    return obj.Title || obj.Name || obj.FullName || obj.FullNameWithParents || obj.Username || ('ID: ' + (obj.Id ?? '?'));
  }

  private getLabel(key: string): string {
    return WgMetadataPanelComponent.LABELS[key] || key.replace(/([A-Z])/g, ' $1').trim();
  }

  formatValue(val: any): string {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Da' : 'Ne';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  }

  /**
   * EntryDateString / LastChangeDateString come as "dd.MM.yyyy HH:mm". new Date() reads that
   * as month.day (US order), so 01.10.2026 became 10 January - parse it explicitly.
   */
  private parseDate(value: string): Date {
    const m = /^\s*(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/.exec(value);
    if (m) {
      return new Date(+m[3], +m[2] - 1, +m[1], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0);
    }
    return new Date(value);
  }

  private formatDate(value: string): string {
    if (!value) return '';
    if (value.includes('. ')) return value;
    try {
      const d = this.parseDate(value);
      if (isNaN(d.getTime())) return value;
      return d.getDate() + '. ' + (d.getMonth() + 1) + '. ' + d.getFullYear();
    } catch {
      return value;
    }
  }

  private formatDateTime(value: string): string {
    if (!value) return '';
    try {
      const d = this.parseDate(value);
      if (isNaN(d.getTime())) return value;
      const pad =(n: number) => n < 10 ? '0' + n : String(n);
      return d.getDate() + '. ' + (d.getMonth() + 1) + '. ' + d.getFullYear() + ', ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
    } catch {
      return value;
    }
  }

  toggleSection(key: string) {
    const s = new Set(this.expandedSections());
    if (s.has(key)) s.delete(key); else s.add(key);
    this.expandedSections.set(s);
  }

  isExpanded(key: string): boolean {
    return this.expandedSections().has(key);
  }
}
